import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api, { formatApiError } from "../lib/api";
import AppLayout from "../components/AppLayout";
import { CATEGORIES, COVER_THEMES } from "../lib/constants";
import { toast } from "sonner";import {
  Loader2, Rocket, ChevronRight, ChevronLeft, Trash2, Plus, User, Calendar as CalIcon,
  FileText, Image as ImageIcon, Video as VideoIcon, Mic, File, LinkIcon, Sparkles, Check,
  AlertTriangle, X, Gift, CreditCard, ShieldAlert
} from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { Calendar } from "../components/ui/calendar";
import { suggestEmail } from "../lib/emailTypo";

import {
  initLocalCapsule, getLocalCapsule, addLocalContent, removeLocalContent,
  previewLocalContent, setLocalFinalMessage, getLocalFinalMessage,
  setLocalMeta, sealAndSubmit, revokeLocalCapsule,
} from "../lib/clientVault";

import { _t, _locale, getLang } from "@/lib/i18n";
import { it as fnsIt, es as fnsEs, enGB as fnsEn } from "date-fns/locale";

const DATE_FNS_LOCALES = { it: fnsIt, es: fnsEs, en: fnsEn };
// Warning concordato (flusso D), mostrato nello Step 6.
const V2_WINDOW_WARNING =
  "La capsula viene cifrata sul tuo dispositivo e inviata al server in forma inviolabile. " +
  "Né noi né nessun altro possiamo leggerla fino alla data di apertura. " +
  "Puoi revocarla gratuitamente entro 24 ore; dopo, verrà sigillata su blockchain in modo permanente.";

function V2WarningBanner() {
  return (
    <div className="mb-6 p-4 rounded-xl border border-yellow-400/30 bg-yellow-400/5 flex items-start gap-3" data-testid="v2-window-warning">
      <ShieldAlert className="w-5 h-5 text-yellow-400 shrink-0 mt-0.5" />
      <div className="text-sm text-white/85 leading-relaxed">
        <strong className="text-yellow-300">{_t("Capsula sigillata solo sul tuo dispositivo.")}</strong>{" "}
        {V2_WINDOW_WARNING}
      </div>
    </div>
  );
}

const STEPS = [
  { id: 1, label: _t("Tipo") },
  { id: 2, label: _t("Destinatari") },
  { id: 3, label: _t("Data") },
  { id: 4, label: _t("Contenuti") },
  { id: 5, label: _t("Messaggio") },
  { id: 6, label: _t("Verifica") },
];

// Shown on every wizard step
function InviolabilityBanner() {
  return (
    <div className="mb-6 p-4 rounded-xl border border-[#FF5722]/30 bg-[#FF5722]/5 flex items-start gap-3" data-testid="inviolability-banner">
      <AlertTriangle className="w-5 h-5 text-[#FF5722] shrink-0 mt-0.5" />
      <div className="text-sm text-white/85 leading-relaxed">
        <strong className="text-[#FF5722]">{_t("Garanzia di inviolabilità.")}</strong>{" "}{_t("Una volta sigillata e inviata, questa capsula")}{" "}<strong className="text-white">{_t("non potrà essere aperta, modificata o recuperata da nessuno")}</strong>{" "}{_t("— nemmeno da te, dal destinatario o dagli amministratori di Time Capsule — fino alla data programmata di apertura.")}</div>
    </div>
  );
}

export default function Wizard() {
  const { id } = useParams();
  const nav = useNavigate();
  const [capsule, setCapsule] = useState(null);
  const [localRec, setLocalRec] = useState(null); // record IndexedDB per v2
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [launchingAnim, setLaunchingAnim] = useState(false);
  const initRef = useRef(false);

  const isV2 = capsule?.security_model === "client_sealed_v2" || (capsule && !capsule.security_model); // default v2 for new drafts

  const reloadLocal = useCallback(async (capId) => {
    const rec = await getLocalCapsule(capId);
    setLocalRec(rec || null);
    return rec;
  }, []);

  // Load or create capsule
  useEffect(() => {
    if (initRef.current) return; // guard double-mount (React StrictMode)
    initRef.current = true;
    (async () => {
      try {
        if (id) {
          const { data } = await api.get(`/capsules/${id}`);
          if (!data.can_edit) {
            toast.error(_t("Questa capsula non è più modificabile"));
            nav(`/capsule/${data.id}`);
            return;
          }
          setCapsule(data);
          // Init / recover il vault locale (idempotente)
          if (data.security_model === "client_sealed_v2" || !data.security_model) {
            await initLocalCapsule(data.id, {
              title: data.title,
              category: data.category,
              open_at: data.open_at,
            });
            await reloadLocal(data.id);
          }
        } else {
          const { data } = await api.post("/capsules", { category: "personalizzata", title: "", cover_theme: "orbit" });
          setCapsule(data);
          // Inizializza il vault locale per la nuova capsula v2
          await initLocalCapsule(data.id, { title: data.title || _t("Capsula"), category: data.category });
          await reloadLocal(data.id);
          window.history.replaceState({}, "", `/wizard/${data.id}`);
        }
      } catch (e) {
        toast.error(formatApiError(e));
        nav("/dashboard");
      } finally {
        setLoading(false);
      }
    })();
  }, []); // eslint-disable-line

  const reload = useCallback(async () => {
    if (!capsule) return;
    const { data } = await api.get(`/capsules/${capsule.id}`);
    setCapsule(data);
  }, [capsule]);

  const saveDraft = async (updates) => {
    setSaving(true);
    try {
      const { data } = await api.patch(`/capsules/${capsule.id}`, {
        category: updates.category ?? capsule.category,
        title: updates.title ?? capsule.title,
        description: updates.description ?? capsule.description,
        cover_theme: updates.cover_theme ?? capsule.cover_theme,
        privacy_level: updates.privacy_level ?? capsule.privacy_level,
        final_message: updates.final_message ?? capsule.final_message,
        timezone_name: updates.timezone_name ?? capsule.timezone_name,
      });
      setCapsule(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const nextStep = () => setStep((s) => Math.min(6, s + 1));
  const prevStep = () => setStep((s) => Math.max(1, s - 1));

  if (loading || !capsule) {
    return <AppLayout><div className="flex items-center justify-center py-24"><Loader2 className="w-8 h-8 animate-spin text-[#FF5722]" /></div></AppLayout>;
  }

  if (launchingAnim) return <LaunchAnimation onDone={() => nav(`/capsule/${capsule.id}`)} />;

  return (
    <AppLayout>
      {/* Step indicator */}
      <div className="flex items-center justify-center mb-10 gap-2 flex-wrap">
        {STEPS.map((s, i) => (
          <div key={s.id} className="flex items-center gap-2">
            <button
              data-testid={`wizard-step-${s.id}`}
              onClick={() => setStep(s.id)}
              className={`step-dot ${step === s.id ? "active" : step > s.id ? "done" : ""}`}
            >
              {step > s.id ? <Check className="w-4 h-4" /> : s.id}
            </button>
            <span className={`text-sm hidden sm:inline ${step === s.id ? "text-white" : "text-white/50"}`}>{s.label}</span>
            {i < STEPS.length - 1 && <div className="w-6 h-px bg-white/10" />}
          </div>
        ))}
      </div>

      <div className="max-w-3xl mx-auto">
        {step === 1 && <Step1 capsule={capsule} saveDraft={saveDraft} onNext={nextStep} />}
        {step === 2 && <Step2 capsule={capsule} reload={reload} onNext={nextStep} onPrev={prevStep} />}
        {step === 3 && <Step3 capsule={capsule} isV2={isV2} onNext={nextStep} onPrev={prevStep} />}
        {step === 4 && <Step4 capsule={capsule} reload={reload} isV2={isV2} localRec={localRec} reloadLocal={reloadLocal} onNext={nextStep} onPrev={prevStep} />}
        {step === 5 && <Step5 capsule={capsule} saveDraft={saveDraft} isV2={isV2} localRec={localRec} reloadLocal={reloadLocal} onNext={nextStep} onPrev={prevStep} />}
        {step === 6 && <Step6 capsule={capsule} isV2={isV2} localRec={localRec} onLaunch={(dt, tz) => launchCapsule(capsule.id, dt, tz, isV2, setLaunchingAnim, nav)} onPrev={prevStep} />}
      </div>

      {saving && <div className="fixed bottom-6 right-6 text-xs text-white/50 flex items-center gap-2"><Loader2 className="w-3 h-3 animate-spin" />{" "}{_t("Salvataggio...")}</div>}
    </AppLayout>
  );
}

async function launchCapsule(id, openDate, tz, isV2, setLaunchingAnim, nav) {
  try {
    if (isV2) {
      // Flusso D: assicuriamoci che l'open_at nel vault locale sia aggiornato
      // (lo Step 3 lo imposta, ma lo ri-forzamo per sicurezza) e sigilliamo
      // immediatamente. Il server riceve subito il bundle cifrato in opaco
      // e passa a RIPENSAMENTO_24H senza anchor on-chain.
      await setLocalMeta(id, { open_at: openDate.toISOString() });
      await sealAndSubmit(api, id);
    } else {
      await api.post(`/capsules/${id}/launch`, { open_at: openDate.toISOString(), timezone_name: tz });
    }
    setLaunchingAnim(true);
  } catch (e) {
    toast.error(formatApiError(e));
  }
}

// -------------- STEP 1: Category & Cover --------------
function Step1({ capsule, saveDraft, onNext }) {
  const [category, setCategory] = useState(capsule.category);
  const [coverTheme, setCoverTheme] = useState(capsule.cover_theme);
  const [title, setTitle] = useState(capsule.title || "");
  const [description, setDescription] = useState(capsule.description || "");
  const [privacyLevel, setPrivacyLevel] = useState(capsule.privacy_level || "private");

  const canContinue = title.trim().length > 0;

  const goNext = async () => {
    await saveDraft({ category, cover_theme: coverTheme, title, description, privacy_level: privacyLevel });
    onNext();
  };

  return (
    <div className="fade-up">
      <div className="mb-8">
        <div className="text-[11px] uppercase tracking-[0.3em] text-[#FF5722] mb-2">{_t("Passo 1 di 6")}</div>
        <h1 className="font-display text-5xl text-white leading-tight mb-3">{_t("Scegli il tipo di capsula")}</h1>
        <p className="text-white/60">{_t("A chi dedicherai questo messaggio? Ogni capsula racconta una storia diversa.")}</p>
      </div>
      <InviolabilityBanner />

      <div className="grid sm:grid-cols-2 gap-3 mb-10">
        {CATEGORIES.map((c) => (
          <button
            key={c.id}
            data-testid={`cat-${c.id}`}
            onClick={() => { setCategory(c.id); setCoverTheme(c.theme); }}
            className={`text-left p-5 rounded-2xl border transition-all duration-300 ${
              category === c.id
                ? "border-[#FF5722] bg-[#FF5722]/10"
                : "border-white/10 hover:border-white/25 bg-white/2"
            }`}
          >
            <div className="text-white font-medium mb-1">{c.label}</div>
            <div className="text-xs text-white/50">{c.desc}</div>
          </button>
        ))}
      </div>

      <div className="space-y-6 mb-10">
        <div>
          <label className="tc-label">{_t("Titolo della capsula")}</label>
          <input data-testid="wizard-title" value={title} onChange={(e) => setTitle(e.target.value)} required maxLength={120} className="tc-input" placeholder={_t("Es. Per il tuo compleanno")} />
        </div>
        <div>
          <label className="tc-label">{_t("Breve descrizione (facoltativa)")}</label>
          <textarea data-testid="wizard-description" value={description} onChange={(e) => setDescription(e.target.value)} className="tc-textarea" rows={3} maxLength={500} placeholder={_t("In poche righe...")} />
        </div>
        <div>
          <label className="tc-label">{_t("Copertina")}</label>
          <div className="grid grid-cols-4 gap-3">
            {COVER_THEMES.map((t) => (
              <button
                key={t.id}
                data-testid={`cover-${t.id}`}
                onClick={() => setCoverTheme(t.id)}
                className={`h-20 rounded-xl ${t.className} border-2 transition-all ${
                  coverTheme === t.id ? "border-[#FF5722] scale-105" : "border-transparent"
                }`}
                title={t.label}
              />
            ))}
          </div>
        </div>
        <div>
          <label className="tc-label">{_t("Privacy")}</label>
          <div className="flex gap-3">
            <button data-testid="privacy-private" onClick={() => setPrivacyLevel("private")} className={`px-4 py-2 rounded-full border text-sm ${privacyLevel === "private" ? "border-[#FF5722] text-[#FF5722]" : "border-white/15 text-white/70"}`}>{_t("Privata")}</button>
            <button data-testid="privacy-shared" onClick={() => setPrivacyLevel("shared")} className={`px-4 py-2 rounded-full border text-sm ${privacyLevel === "shared" ? "border-[#FF5722] text-[#FF5722]" : "border-white/15 text-white/70"}`}>{_t("Condivisa")}</button>
          </div>
        </div>
      </div>

      <div className="flex justify-end">
        <button data-testid="wizard-step1-next" disabled={!canContinue} onClick={goNext} className="btn-launch">{_t("Continua")}{" "}<ChevronRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}

// -------------- STEP 2: Recipients --------------
function Step2({ capsule, reload, onNext, onPrev }) {
  const [recipients, setRecipients] = useState(capsule.recipients || []);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ name: "", surname: "", email: "", phone: "", relationship: "", personal_message: "", invited_now: false });

  useEffect(() => { setRecipients(capsule.recipients || []); }, [capsule.recipients]);

  const [typoOk, setTypoOk] = useState("");
  const suggestion = suggestEmail(form.email);
  const showTypo = suggestion && typoOk !== form.email;

  const add = async () => {
    if (!form.name || !form.email) { toast.error(_t("Nome ed email sono obbligatori")); return; }
    if (showTypo) { toast.error(_t("Controlla l'indirizzo email prima di aggiungerlo")); return; }
    setAdding(true);
    try {
      await api.post(`/capsules/${capsule.id}/recipients`, form);
      await reload();
      setForm({ name: "", surname: "", email: "", phone: "", relationship: "", personal_message: "", invited_now: false });
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setAdding(false);
    }
  };

  const remove = async (rid) => {
    try {
      await api.delete(`/capsules/${capsule.id}/recipients/${rid}`);
      await reload();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="fade-up">
      <div className="mb-8">
        <div className="text-[11px] uppercase tracking-[0.3em] text-[#FF5722] mb-2">{_t("Passo 2 di 6")}</div>
        <h1 className="font-display text-5xl text-white leading-tight mb-3">{_t("Scegli i destinatari")}</h1>
        <p className="text-white/60">{_t("Chi riceverà questa capsula? Puoi aggiungerne più di uno.")}</p>
      </div>
      <InviolabilityBanner />

      {recipients.length > 0 && (
        <ul className="space-y-2 mb-8" data-testid="recipients-list">
          {recipients.map((r) => (
            <li key={r.id} className="flex items-center justify-between p-4 rounded-xl border border-white/10 bg-white/2">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-white/5 flex items-center justify-center"><User className="w-5 h-5 text-white/60" /></div>
                <div>
                  <div className="text-white text-sm">{r.name} {r.surname} {r.relationship && <span className="text-white/40 text-xs">· {r.relationship}</span>}</div>
                  <div className="text-white/50 text-xs">{r.email}{r.phone && ` · ${r.phone}`}</div>
                </div>
              </div>
              <button data-testid={`remove-recipient-${r.id}`} onClick={() => remove(r.id)} className="text-white/40 hover:text-red-400 p-2"><Trash2 className="w-4 h-4" /></button>
            </li>
          ))}
        </ul>
      )}

      <div className="p-6 rounded-2xl border border-white/10 bg-white/2 mb-10">
        <div className="text-white font-medium mb-4 flex items-center gap-2"><Plus className="w-4 h-4 text-[#FF5722]" />{" "}{_t("Aggiungi destinatario")}</div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div><label className="tc-label">{_t("Nome *")}</label><input data-testid="rec-name" value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} className="tc-input" /></div>
          <div><label className="tc-label">{_t("Cognome")}</label><input data-testid="rec-surname" value={form.surname} onChange={(e) => setForm({...form, surname: e.target.value})} className="tc-input" /></div>
          <div><label className="tc-label">{_t("Email *")}</label><input data-testid="rec-email" type="email" value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} className="tc-input" />
            {showTypo && (
              <div data-testid="rec-email-typo" className="mt-2 p-3 rounded-lg border border-amber-400/40 bg-amber-400/10 text-xs text-amber-100">{_t("Forse intendevi")}{" "}<strong data-testid="rec-email-suggestion">{suggestion}</strong>?
                <div className="flex gap-2 mt-2">
                  <button type="button" data-testid="rec-email-use-suggestion" onClick={() => setForm({ ...form, email: suggestion })} className="px-3 py-1 rounded-full bg-[#FF5722] text-white">{_t("Correggi")}</button>
                  <button type="button" data-testid="rec-email-keep" onClick={() => setTypoOk(form.email)} className="px-3 py-1 rounded-full border border-white/20 text-white/80">{_t("È giusto così")}</button>
                </div>
              </div>
            )}
          </div>
          <div><label className="tc-label">{_t("Telefono (facoltativo)")}</label><input data-testid="rec-phone" value={form.phone} onChange={(e) => setForm({...form, phone: e.target.value})} className="tc-input" /></div>
          <div className="sm:col-span-2"><label className="tc-label">{_t("Relazione (es. figlio, amico, collega)")}</label><input data-testid="rec-relationship" value={form.relationship} onChange={(e) => setForm({...form, relationship: e.target.value})} className="tc-input" /></div>
          <div className="sm:col-span-2"><label className="tc-label">{_t("Messaggio personale (facoltativo)")}</label><textarea data-testid="rec-message" value={form.personal_message} onChange={(e) => setForm({...form, personal_message: e.target.value})} rows={2} className="tc-textarea !min-h-[80px]" placeholder={_t("Un pensiero solo per questa persona...")} /></div>
          <div className="sm:col-span-2">
            <label className="flex items-start gap-2 cursor-pointer">
              <input data-testid="rec-invitenow" type="checkbox" checked={form.invited_now} onChange={(e) => setForm({...form, invited_now: e.target.checked})} className="mt-1 accent-[#FF5722]" />
              <span className="text-sm text-white/80">{_t("Invita subito il destinatario (altrimenti verrà avvisato solo alla data di apertura)")}</span>
            </label>
          </div>
        </div>
        <div className="flex justify-end mt-4">
          <button data-testid="add-recipient-btn" onClick={add} disabled={adding} className="btn-launch">{_t("{0} Aggiungi", adding ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />)}</button>
        </div>
      </div>

      <div className="flex justify-between">
        <button data-testid="wizard-step2-prev" onClick={onPrev} className="btn-ghost"><ChevronLeft className="w-4 h-4" />{" "}{_t("Indietro")}</button>
        <button data-testid="wizard-step2-next" disabled={recipients.length === 0} onClick={onNext} className="btn-launch">{_t("Continua")}{" "}<ChevronRight className="w-4 h-4" /></button>
      </div>
    </div>
  );
}

// -------------- STEP 3: Date --------------
function Step3({ capsule, isV2, onNext, onPrev }) {
  const [selected, setSelected] = useState(capsule.open_at ? new Date(capsule.open_at) : null);
  const [time, setTime] = useState(capsule.open_at ? new Date(capsule.open_at).toTimeString().slice(0, 5) : "10:00");
  const [preset, setPreset] = useState(null);

  const applyPreset = (kind) => {
    const now = new Date();
    let d = new Date();
    if (kind === "1d") d.setDate(now.getDate() + 2);
    else if (kind === "1m") d.setMonth(now.getMonth() + 1);
    else if (kind === "1y") d.setFullYear(now.getFullYear() + 1);
    else if (kind === "10y") d.setFullYear(now.getFullYear() + 10);
    else if (kind === "20y") d.setFullYear(now.getFullYear() + 20);
    else if (kind === "30y") d.setFullYear(now.getFullYear() + 30);
    setSelected(d);
    setPreset(kind);
  };

  const goNext = async () => {
    if (!selected) { toast.error(_t("Scegli una data di apertura")); return; }
    // combine
    const [hh, mm] = time.split(":").map(Number);
    const combined = new Date(selected);
    combined.setHours(hh || 0, mm || 0, 0, 0);
    if (combined <= new Date(Date.now() + 24 * 3600_000 + 60_000)) {
      toast.error(_t("La data di apertura deve essere almeno 24 ore dopo il lancio"));
      return;
    }
    // Store in local component; actual value passed to step 6 via capsule refetch or wizard state
    // We just pass via query string / local state; simplest is to set open_at via a launch preview action.
    localStorage.setItem(`tc_pending_open_${capsule.id}`, combined.toISOString());
    localStorage.setItem(`tc_pending_tz_${capsule.id}`, Intl.DateTimeFormat().resolvedOptions().timeZone || "Europe/Rome");
    if (isV2) {
      try { await setLocalMeta(capsule.id, { open_at: combined.toISOString() }); } catch (_e) {}
    }
    onNext();
  };

  const presets = [
    { id: "1d", label: _t("Domani") },
    { id: "1m", label: _t("1 mese") },
    { id: "1y", label: _t("1 anno") },
    { id: "10y", label: _t("10 anni") },
    { id: "20y", label: _t("20 anni") },
    { id: "30y", label: _t("30 anni") },
  ];

  return (
    <div className="fade-up">
      <div className="mb-8">
        <div className="text-[11px] uppercase tracking-[0.3em] text-[#FF5722] mb-2">{_t("Passo 3 di 6")}</div>
        <h1 className="font-display text-5xl text-white leading-tight mb-3">{_t("Quando aprirla?")}</h1>
        <p className="text-white/60">{_t("Scegli il momento in cui la capsula si aprirà automaticamente per il destinatario.")}</p>
      </div>
      <InviolabilityBanner />

      <div className="flex flex-wrap gap-2 mb-8" data-testid="date-presets">
        {presets.map((p) => (
          <button key={p.id} onClick={() => applyPreset(p.id)} data-testid={`preset-${p.id}`}
            className={`px-4 py-2 rounded-full border text-sm transition-colors ${preset === p.id ? "border-[#FF5722] text-[#FF5722]" : "border-white/15 text-white/80 hover:border-white/30"}`}>
            {p.label}
          </button>
        ))}
      </div>

      <div className="grid sm:grid-cols-2 gap-6 mb-10">
        <div>
          <label className="tc-label">{_t("Data personalizzata")}</label>
          <Popover>
            <PopoverTrigger asChild>
              <button data-testid="wizard-date-picker" type="button" className="tc-input text-left flex items-center gap-3">
                <CalIcon className="w-4 h-4 text-[#FF5722]" />
                {selected ? selected.toLocaleDateString(_locale(), {day:"2-digit",month:"long",year:"numeric"}) : <span className="text-white/40">{_t("Scegli data")}</span>}
              </button>
            </PopoverTrigger>
            <PopoverContent className="p-0 bg-[#0F1A2E] border-white/10" align="start">
              <Calendar mode="single" locale={DATE_FNS_LOCALES[getLang()]} selected={selected} onSelect={setSelected} disabled={(d) => d < new Date()} initialFocus />
            </PopoverContent>
          </Popover>
        </div>
        <div>
          <label className="tc-label">{_t("Ora")}</label>
          <input data-testid="wizard-time" type="time" value={time} onChange={(e) => setTime(e.target.value)} className="tc-input" />
        </div>
      </div>

      {selected && (
        <div className="p-4 rounded-xl bg-[#33B2FF]/8 border border-[#33B2FF]/25 text-sm text-white/80 mb-8 flex items-start gap-3">
          <Sparkles className="w-4 h-4 text-[#33B2FF] mt-0.5 shrink-0" />
          <div>{_t("La capsula si aprirà il")}{" "}<strong className="text-white">{selected.toLocaleDateString(_locale(), {day:"2-digit",month:"long",year:"numeric"})}</strong>{" "}{_t("alle")}{" "}<strong className="text-white">{time}</strong> ({Intl.DateTimeFormat().resolvedOptions().timeZone}).
            <div className="text-xs text-white/50 mt-1">{_t("I contenuti resteranno sigillati fino a quel momento.")}</div>
          </div>
        </div>
      )}

      <div className="flex justify-between">
        <button data-testid="wizard-step3-prev" onClick={onPrev} className="btn-ghost"><ChevronLeft className="w-4 h-4" />{" "}{_t("Indietro")}</button>
        <button data-testid="wizard-step3-next" onClick={goNext} className="btn-launch">{_t("Continua")}{" "}<ChevronRight className="w-4 h-4" /></button>
      </div>
    </div>
  );
}

// -------------- STEP 4: Contents --------------
function Step4({ capsule, reload, isV2, localRec, reloadLocal, onNext, onPrev }) {
  // Per capsule v2 i contenuti sono solo in IndexedDB. Per legacy usiamo l'API server.
  const legacyContents = capsule.contents || [];
  const localContents = (localRec?.contents || []).map((c) => ({
    id: c.id,
    kind: c.kind,
    title: c.title,
    size: c.size,
    original_filename: c.filename,
    text_body: null,
    url: null,
  }));
  const contents = isV2 ? localContents : legacyContents;

  const [textForm, setTextForm] = useState({ title: "", text_body: "" });
  const [linkForm, setLinkForm] = useState({ title: "", url: "" });
  const [uploading, setUploading] = useState(false);

  const addText = async () => {
    if (!textForm.text_body.trim()) { toast.error(_t("Scrivi qualcosa")); return; }
    try {
      if (isV2) {
        await addLocalContent(capsule.id, { kind: "text", title: textForm.title, text: textForm.text_body });
        await reloadLocal(capsule.id);
      } else {
        await api.post(`/capsules/${capsule.id}/contents/text`, { kind: "text", title: textForm.title, text_body: textForm.text_body });
        await reload();
      }
      setTextForm({ title: "", text_body: "" });
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const addLink = async () => {
    if (!linkForm.url.trim()) { toast.error(_t("Inserisci un URL")); return; }
    try {
      if (isV2) {
        // Codifichiamo il link come text con prefisso 'link:' per non toccare l'API server.
        await addLocalContent(capsule.id, {
          kind: "link",
          title: linkForm.title || linkForm.url,
          text: linkForm.url,
        });
        await reloadLocal(capsule.id);
      } else {
        await api.post(`/capsules/${capsule.id}/contents/text`, { kind: "link", title: linkForm.title, url: linkForm.url });
        await reload();
      }
      setLinkForm({ title: "", url: "" });
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const uploadFile = async (file, kind) => {
    setUploading(true);
    try {
      if (isV2) {
        await addLocalContent(capsule.id, { kind, title: file.name, file });
        await reloadLocal(capsule.id);
      } else {
        const fd = new FormData();
        fd.append("kind", kind);
        fd.append("title", "");
        fd.append("description", "");
        fd.append("file", file);
        await api.post(`/capsules/${capsule.id}/contents/file`, fd, { headers: { "Content-Type": "multipart/form-data" } });
        await reload();
      }
      toast.success(`${file.name} aggiunto`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setUploading(false); }
  };

  const remove = async (cid) => {
    try {
      if (isV2) {
        await removeLocalContent(capsule.id, cid);
        await reloadLocal(capsule.id);
      } else {
        await api.delete(`/capsules/${capsule.id}/contents/${cid}`);
        await reload();
      }
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="fade-up">
      <div className="mb-8">
        <div className="text-[11px] uppercase tracking-[0.3em] text-[#FF5722] mb-2">{_t("Passo 4 di 6")}</div>
        <h1 className="font-display text-5xl text-white leading-tight mb-3">{_t("Aggiungi contenuti")}</h1>
        <p className="text-white/60">{_t("Testo, foto, video, audio, documenti e link. Ordinali come preferisci.")}</p>
      </div>
      <InviolabilityBanner />

      {/* Upload buttons */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-8">
        <UploadTile icon={<ImageIcon />} label={_t("Foto")} accept="image/*" kind="photo" onSelect={uploadFile} testid="up-photo" />
        <UploadTile icon={<VideoIcon />} label={_t("Video")} accept="video/*" kind="video" onSelect={uploadFile} testid="up-video" />
        <UploadTile icon={<Mic />} label={_t("Audio")} accept="audio/*" kind="audio" onSelect={uploadFile} testid="up-audio" />
        <UploadTile icon={<File />} label={_t("Documento")} accept=".pdf,.doc,.docx,.txt,.xls,.xlsx,.ppt,.pptx,.zip,.csv" kind="document" onSelect={uploadFile} testid="up-document" />
      </div>
      {uploading && <div className="text-xs text-white/50 mb-4 flex items-center gap-2"><Loader2 className="w-3 h-3 animate-spin" />{" "}{_t("Caricamento in corso...")}</div>}

      {/* Text/Link forms */}
      <div className="grid md:grid-cols-2 gap-4 mb-8">
        <div className="p-5 rounded-2xl border border-white/10 bg-white/2">
          <div className="text-white font-medium mb-3 flex items-center gap-2"><FileText className="w-4 h-4 text-[#FF5722]" />{" "}{_t("Aggiungi testo/lettera")}</div>
          <input data-testid="text-title" value={textForm.title} onChange={(e) => setTextForm({...textForm, title: e.target.value})} className="tc-input mb-3" placeholder={_t("Titolo (facoltativo)")} />
          <textarea data-testid="text-body" value={textForm.text_body} onChange={(e) => setTextForm({...textForm, text_body: e.target.value})} className="tc-textarea mb-3" rows={4} placeholder={_t("Scrivi il tuo messaggio...")} />
          <button data-testid="add-text-btn" onClick={addText} className="btn-launch !text-sm !py-2">{_t("Aggiungi testo")}</button>
        </div>
        <div className="p-5 rounded-2xl border border-white/10 bg-white/2">
          <div className="text-white font-medium mb-3 flex items-center gap-2"><LinkIcon className="w-4 h-4 text-[#FF5722]" />{" "}{_t("Aggiungi link")}</div>
          <input data-testid="link-title" value={linkForm.title} onChange={(e) => setLinkForm({...linkForm, title: e.target.value})} className="tc-input mb-3" placeholder={_t("Etichetta (facoltativa)")} />
          <input data-testid="link-url" value={linkForm.url} onChange={(e) => setLinkForm({...linkForm, url: e.target.value})} className="tc-input mb-3" placeholder="https://..." />
          <button data-testid="add-link-btn" onClick={addLink} className="btn-launch !text-sm !py-2">{_t("Aggiungi link")}</button>
        </div>
      </div>

      {/* Content list */}
      {contents.length > 0 ? (
        <ul className="space-y-2 mb-6" data-testid="content-list">
          {contents.map((c) => (
            <li key={c.id} className="flex items-center justify-between p-4 rounded-xl border border-white/10 bg-white/2">
              <div className="flex items-center gap-3 min-w-0">
                {c.kind === "text" ? <FileText className="w-4 h-4 text-[#FF5722] shrink-0" /> :
                 c.kind === "photo" ? <ImageIcon className="w-4 h-4 text-[#FF5722] shrink-0" /> :
                 c.kind === "video" ? <VideoIcon className="w-4 h-4 text-[#FF5722] shrink-0" /> :
                 c.kind === "audio" ? <Mic className="w-4 h-4 text-[#FF5722] shrink-0" /> :
                 c.kind === "link" ? <LinkIcon className="w-4 h-4 text-[#FF5722] shrink-0" /> :
                 c.kind === "voucher" ? <Gift className="w-4 h-4 text-[#FF5722] shrink-0" /> :
                 <File className="w-4 h-4 text-[#FF5722] shrink-0" />}
                <div className="min-w-0">
                  <div className="text-sm text-white truncate">{c.title || c.original_filename || (c.kind === "text" ? c.text_body?.slice(0, 60) : c.url)}</div>
                  <div className="text-xs text-white/40 uppercase tracking-wide">{c.kind}{c.size ? ` · ${(c.size/1024/1024).toFixed(1)} MB` : ""}</div>
                </div>
              </div>
              <button data-testid={`remove-content-${c.id}`} onClick={() => remove(c.id)} className="text-white/40 hover:text-red-400 p-2"><Trash2 className="w-4 h-4" /></button>
            </li>
          ))}
        </ul>
      ) : (
        <div className="text-center py-8 text-white/40 text-sm mb-6">{_t("Nessun contenuto ancora. Aggiungine almeno uno.")}</div>
      )}

      {/* Voucher gift section */}
      <VoucherGift capsuleId={capsule.id} contents={contents} reload={reload} />

      <div className="flex justify-between mt-6">
        <button data-testid="wizard-step4-prev" onClick={onPrev} className="btn-ghost"><ChevronLeft className="w-4 h-4" />{" "}{_t("Indietro")}</button>
        <button data-testid="wizard-step4-next" onClick={onNext} className="btn-launch">{_t("Continua")}{" "}<ChevronRight className="w-4 h-4" /></button>
      </div>
    </div>
  );
}

// -------------- VoucherGift subcomponent (Step 4) --------------
function VoucherGift({ capsuleId, contents, reload }) {
  const [config, setConfig] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [amount, setAmount] = useState(50);
  const [customAmount, setCustomAmount] = useState("");
  const [purpose, setPurpose] = useState("regalo");
  const [bName, setBName] = useState("");
  const [bSurname, setBSurname] = useState("");
  const [bEmail, setBEmail] = useState("");
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [confirmedTerms, setConfirmedTerms] = useState(false);

  const existingVoucher = contents.find((c) => c.kind === "voucher");

  useEffect(() => {
    api.get("/vouchers/config").then(({ data }) => setConfig(data)).catch(() => {});
  }, []);

  const finalAmount = customAmount ? Number(customAmount) : Number(amount);
  const fee = config ? +(finalAmount * config.fee_pct).toFixed(2) : 0;
  const total = +(finalAmount + fee).toFixed(2);
  const canSubmit = finalAmount > 0 && bName && bEmail && confirmedTerms && !submitting;

  const submit = async () => {
    setSubmitting(true);
    try {
      const { data } = await api.post("/vouchers/orders", {
        capsule_id: capsuleId,
        amount: finalAmount,
        purpose,
        beneficiary_name: bName,
        beneficiary_surname: bSurname,
        beneficiary_email: bEmail,
        personal_message: message,
        origin_url: window.location.origin,
      });
      // Redirect to Stripe checkout
      if (data.checkout_url) {
        window.location.href = data.checkout_url;
      } else {
        toast.error(_t("URL di pagamento non disponibile"));
      }
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setSubmitting(false); }
  };

  if (!config) return null;

  return (
    <div className="p-6 rounded-2xl border border-[#FF5722]/30 bg-gradient-to-br from-[#FF5722]/10 to-transparent mb-6" data-testid="voucher-gift-section">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-[#FF5722]/20 flex items-center justify-center"><Gift className="w-5 h-5 text-[#FF5722]" /></div>
          <div>
            <div className="text-white font-medium">{_t("Aggiungi un regalo futuro")}</div>
            <div className="text-xs text-white/60">{_t("Un voucher prepagato riscattabile dal beneficiario alla data di apertura.")}</div>
          </div>
        </div>
        {!existingVoucher && !showForm && (
          <button onClick={() => setShowForm(true)} className="btn-launch !text-sm !py-2" data-testid="add-voucher-btn">
            <Plus className="w-4 h-4" />{" "}{_t("Aggiungi voucher")}</button>
        )}
      </div>

      {existingVoucher && (
        <div className="mt-3 p-4 rounded-lg bg-white/5 border border-white/10 text-sm text-white">
          <div className="flex items-center gap-2">
            <Gift className="w-4 h-4 text-[#FF5722]" />
            <span>{_t("Voucher già aggiunto alla capsula:")}{" "}<strong>{existingVoucher.title}</strong></span>
          </div>
          <div className="text-xs text-white/50 mt-1">{_t("Rimuovilo dalla lista sopra se vuoi cambiarlo.")}</div>
        </div>
      )}

      {showForm && !existingVoucher && (
        <div className="mt-4 space-y-5">
          <div className="text-[11px] uppercase tracking-[0.3em] text-red-300 bg-red-500/10 border border-red-500/30 rounded-lg p-3">{_t("⚠ Modalità DEMO — Il pagamento è simulato con Stripe test. Nessun denaro reale.")}</div>

          {/* Amount */}
          <div>
            <label className="tc-label">{_t("Importo")}</label>
            <div className="flex flex-wrap gap-2 mb-2">
              {config.fixed_amounts.map((a) => (
                <button type="button" key={a} onClick={() => { setAmount(a); setCustomAmount(""); }} data-testid={`voucher-amount-${a}`}
                  className={`px-4 py-2 rounded-full border text-sm ${!customAmount && amount === a ? "border-[#FF5722] text-[#FF5722]" : "border-white/15 text-white/80"}`}>
                  € {a.toFixed(0)}
                </button>
              ))}
            </div>
            <input data-testid="voucher-custom-amount" type="number" min={config.min_amount} max={config.max_amount} step="1"
              value={customAmount} onChange={(e) => setCustomAmount(e.target.value)}
              placeholder={_t("Importo personalizzato (min € {0} - max € {1})", config.min_amount, config.max_amount)}
              className="tc-input" />
          </div>

          {/* Purpose */}
          <div>
            <label className="tc-label">{_t("Occasione")}</label>
            <select data-testid="voucher-purpose" value={purpose} onChange={(e) => setPurpose(e.target.value)} className="tc-input">
              {config.purposes.map((p) => <option key={p.id} value={p.id}>{p.label}</option>)}
            </select>
          </div>

          {/* Beneficiary */}
          <div className="grid sm:grid-cols-2 gap-4">
            <div><label className="tc-label">{_t("Nome beneficiario *")}</label>
              <input data-testid="voucher-bname" value={bName} onChange={(e) => setBName(e.target.value)} className="tc-input" /></div>
            <div><label className="tc-label">{_t("Cognome")}</label>
              <input data-testid="voucher-bsurname" value={bSurname} onChange={(e) => setBSurname(e.target.value)} className="tc-input" /></div>
            <div className="sm:col-span-2"><label className="tc-label">{_t("Email beneficiario *")}</label>
              <input data-testid="voucher-bemail" type="email" value={bEmail} onChange={(e) => setBEmail(e.target.value)} className="tc-input" /></div>
            <div className="sm:col-span-2"><label className="tc-label">{_t("Messaggio personale")}</label>
              <textarea data-testid="voucher-message" value={message} onChange={(e) => setMessage(e.target.value)} rows={2} className="tc-textarea !min-h-[80px]" placeholder={_t("Un pensiero per il beneficiario...")} /></div>
          </div>

          {/* Summary */}
          <div className="p-4 rounded-xl bg-white/5 border border-white/10 space-y-2 text-sm">
            <div className="flex justify-between"><span className="text-white/60">{_t("Valore voucher")}</span><span className="text-white">€ {finalAmount.toFixed(2)}</span></div>
            <div className="flex justify-between"><span className="text-white/60">{_t("Commissione ({0}%)", (config.fee_pct*100).toFixed(0))}</span><span className="text-white">€ {fee.toFixed(2)}</span></div>
            <div className="flex justify-between border-t border-white/10 pt-2 mt-2"><span className="text-white font-medium">{_t("Totale da pagare")}</span><span className="text-[#FF5722] font-bold text-lg">€ {total.toFixed(2)}</span></div>
            <div className="text-xs text-white/50 pt-2">{_t("Il beneficiario riceverà il valore nominale (€ {0}) alla data di apertura. La banca depositaria è {1} (demo).", finalAmount.toFixed(2), config.bank.name)}</div>
          </div>

          <label className="flex items-start gap-2 cursor-pointer">
            <input data-testid="voucher-confirm-terms" type="checkbox" checked={confirmedTerms} onChange={(e) => setConfirmedTerms(e.target.checked)} className="mt-1 accent-[#FF5722]" />
            <span className="text-sm text-white/80">{_t("Accetto i termini di servizio e prendo atto che, dopo il lancio della capsula, il voucher non è modificabile. Solo il beneficiario autenticato potrà riscuoterlo alla data di apertura.")}</span>
          </label>

          <div className="flex justify-end gap-2">
            <button type="button" onClick={() => setShowForm(false)} className="btn-ghost">{_t("Annulla")}</button>
            <button type="button" data-testid="voucher-pay-btn" onClick={submit} disabled={!canSubmit} className="btn-launch">
              {submitting ? <><Loader2 className="w-4 h-4 animate-spin" />{" "}{_t("Vado al pagamento...")}</> : <><CreditCard className="w-4 h-4" />{" "}{_t("Paga €")}{" "}{total.toFixed(2)}</>}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function UploadTile({ icon, label, accept, kind, onSelect, testid }) {
  const inputId = `file-${kind}`;
  return (
    <label htmlFor={inputId} data-testid={testid} className="cursor-pointer flex flex-col items-center gap-2 py-6 border border-dashed border-white/15 hover:border-[#FF5722] rounded-xl transition-colors">
      <input id={inputId} type="file" accept={accept} onChange={(e) => {
        const f = e.target.files?.[0]; if (f) onSelect(f, kind); e.target.value = "";
      }} className="hidden" />
      <div className="text-[#FF5722]">{icon}</div>
      <div className="text-xs text-white/70">{label}</div>
    </label>
  );
}

// -------------- STEP 5: Final message --------------
function Step5({ capsule, saveDraft, isV2, localRec, reloadLocal, onNext, onPrev }) {
  const [msg, setMsg] = useState("");

  // Carica il final_message locale (v2) o quello server (legacy)
  useEffect(() => {
    (async () => {
      if (isV2) {
        try {
          const text = await getLocalFinalMessage(capsule.id);
          setMsg(text || "");
        } catch (_e) { setMsg(""); }
      } else {
        setMsg(capsule.final_message || "");
      }
    })();
    // eslint-disable-next-line
  }, [isV2, capsule.id]);

  const save = async () => {
    if (isV2) {
      try {
        await setLocalFinalMessage(capsule.id, msg);
        await reloadLocal(capsule.id);
        onNext();
      } catch (e) { toast.error(formatApiError(e)); }
    } else {
      await saveDraft({ final_message: msg });
      onNext();
    }
  };

  return (
    <div className="fade-up">
      <div className="mb-8">
        <div className="text-[11px] uppercase tracking-[0.3em] text-[#FF5722] mb-2">{_t("Passo 5 di 6")}</div>
        <h1 className="font-display text-5xl text-white leading-tight mb-3">{_t("Un ultimo pensiero")}</h1>
        <p className="text-white/60">{_t("Il messaggio finale che il destinatario leggerà come apertura della capsula.")}</p>
      </div>
      <InviolabilityBanner />

      <div className="mb-8">
        <label className="tc-label">{_t("Messaggio finale (facoltativo)")}</label>
        <textarea data-testid="final-message" value={msg} onChange={(e) => setMsg(e.target.value)} className="tc-textarea !min-h-[200px]" rows={8} placeholder={_t("Cosa vorresti dire come ultima cosa?")} />
      </div>

      {msg && (
        <div className="p-6 rounded-2xl border border-white/10 bg-white/2 mb-10">
          <div className="text-[10px] uppercase tracking-[0.3em] text-white/40 mb-3">{_t("Anteprima")}</div>
          <div className="font-hand text-3xl text-white leading-relaxed whitespace-pre-wrap">{msg}</div>
        </div>
      )}

      <div className="flex justify-between">
        <button data-testid="wizard-step5-prev" onClick={onPrev} className="btn-ghost"><ChevronLeft className="w-4 h-4" />{" "}{_t("Indietro")}</button>
        <button data-testid="wizard-step5-next" onClick={save} className="btn-launch">{_t("Continua")}{" "}<ChevronRight className="w-4 h-4" /></button>
      </div>
    </div>
  );
}

// -------------- STEP 6: Review & Launch --------------
function Step6({ capsule, isV2, localRec, onLaunch, onPrev }) {
  const openIso = localStorage.getItem(`tc_pending_open_${capsule.id}`);
  const tz = localStorage.getItem(`tc_pending_tz_${capsule.id}`) || "Europe/Rome";
  const openDate = openIso ? new Date(openIso) : null;
  const [confirmedInviolability, setConfirmedInviolability] = useState(false);
  const [confirmedBlockchain, setConfirmedBlockchain] = useState(false);
  const [confirmedLocalRisk, setConfirmedLocalRisk] = useState(false);

  // Per v2 contiamo contenuti locali e final_message locale.
  const v2ContentCount = (localRec?.contents?.length || 0) + (localRec?.finalMessage ? 1 : 0);
  const hasContent = isV2 ? v2ContentCount > 0 : (capsule.content_count > 0 || (capsule.final_message || "").trim());

  const canLaunch = openDate && confirmedInviolability && confirmedBlockchain &&
    (!isV2 || confirmedLocalRisk) &&
    (capsule.recipients_count > 0) && hasContent;

  return (
    <div className="fade-up">
      <div className="mb-8">
        <div className="text-[11px] uppercase tracking-[0.3em] text-[#FF5722] mb-2">{_t("Passo 6 di 6")}</div>
        <h1 className="font-display text-5xl text-white leading-tight mb-3">{_t("Verifica e lancia")}</h1>
        <p className="text-white/60">{_t("Controlla il riepilogo. Dopo il lancio avrai 24 ore per ripensarci.")}</p>
      </div>

      <div className="grid md:grid-cols-2 gap-4 mb-8">
        <Summary label={_t("Titolo")} value={capsule.title} />
        <Summary label={_t("Categoria")} value={CATEGORIES.find(c => c.id === capsule.category)?.label} />
        <Summary label={_t("Destinatari")} value={`${capsule.recipients_count}`} />
        <Summary label={_t("Contenuti")} value={`${isV2 ? v2ContentCount : capsule.content_count}`} />
        <Summary label={_t("Data di apertura")} value={openDate ? openDate.toLocaleDateString(_locale(), {day:"2-digit",month:"long",year:"numeric",hour:"2-digit",minute:"2-digit"}) : "Non impostata"} />
        <Summary label={_t("Fuso orario")} value={tz} />
        <Summary label={_t("Privacy")} value={capsule.privacy_level === "shared" ? "Condivisa" : "Privata"} />
        <Summary label={_t("Spazio usato")} value={`${((capsule.storage_used||0)/1024/1024).toFixed(1)} MB`} />
      </div>

      {/* Warnings */}
      {(!openDate || capsule.recipients_count === 0 || !hasContent) && (
        <div className="mb-6 p-4 rounded-xl border border-yellow-500/30 bg-yellow-500/10 text-yellow-200 text-sm flex items-start gap-3">
          <AlertTriangle className="w-4 h-4 mt-0.5" />
          <div>
            {!openDate && <div>{_t("Torna al passo 3 e imposta la data di apertura.")}</div>}
            {capsule.recipients_count === 0 && <div>{_t("Torna al passo 2 e aggiungi almeno un destinatario.")}</div>}
            {!hasContent && <div>{_t("Torna al passo 4 o 5 e aggiungi almeno un contenuto o un messaggio finale.")}</div>}
          </div>
        </div>
      )}

      {/* V2 — warning sulla permanenza locale durante le 24h */}
      {isV2 && <V2WarningBanner />}

      {isV2 && (
        <div className="p-5 rounded-2xl border border-yellow-400/40 bg-yellow-400/5 mb-4" data-testid="v2-risk-confirm">
          <label className="flex items-start gap-3 cursor-pointer">
            <input data-testid="launch-confirm-local-risk" type="checkbox" checked={confirmedLocalRisk} onChange={(e) => setConfirmedLocalRisk(e.target.checked)} className="mt-1 accent-yellow-400" />
            <span className="text-sm text-white">{_t("Confermo di aver letto che la capsula resta")}{" "}<strong>{_t("esclusivamente")}</strong>{" "}{_t("su questo dispositivo per 24 ore e che la perdita del dispositivo o la cancellazione dei dati dell'app prima del sigillo definitivo rendono la capsula irrecuperabile.")}</span>
          </label>
        </div>
      )}

      {/* Inviolability confirmation (explicit checkbox) */}
      <div className="p-5 rounded-2xl border border-[#FF5722]/40 bg-[#FF5722]/8 mb-4" data-testid="inviolability-final">
        <div className="flex items-start gap-3 mb-4">
          <Rocket className="w-5 h-5 text-[#FF5722] shrink-0 mt-0.5 -rotate-45" />
          <div>
            <div className="text-white font-medium mb-2">{_t("Garanzia di inviolabilità")}</div>
            <p className="text-sm text-white/75 leading-relaxed">{_t("Al lancio la capsula entrerà nel")}{" "}<strong className="text-[#FF5722]">{_t("periodo di ripensamento di 24 ore")}</strong>{_t(". Trascorso questo periodo, la capsula sarà")}{" "}<strong className="text-white">{_t("sigillata definitivamente")}</strong>: <strong className="text-white">{_t("non potrà più essere aperta, modificata o recuperata da nessuno")}</strong>{" "}{_t("— nemmeno da te, dal destinatario o dagli amministratori di Time Capsule —")}{" "}<strong className="text-white">{_t("fino alla data programmata di apertura")}</strong>.
            </p>
          </div>
        </div>
        <label className="flex items-start gap-3 cursor-pointer">
          <input data-testid="launch-confirm-inviolability" type="checkbox" checked={confirmedInviolability} onChange={(e) => setConfirmedInviolability(e.target.checked)} className="mt-1 accent-[#FF5722]" />
          <span className="text-sm text-white">{_t("Accetto la garanzia di inviolabilità: la capsula sarà accessibile")}{" "}<strong>{_t("solo")}</strong>{" "}{_t("al destinatario,")}{" "}<strong>{_t("solo")}</strong>{" "}{_t("alla data programmata. Nessuno — inclusi mittente, destinatario e amministratori — potrà anticipare, modificare o annullare l'apertura dopo il periodo di ripensamento.")}</span>
        </label>
      </div>

      {/* Blockchain notice + confirm */}
      <div className="p-5 rounded-2xl border border-[#33B2FF]/40 bg-[#33B2FF]/8 mb-8">
        <div className="flex items-start gap-3 mb-4">
          <Sparkles className="w-5 h-5 text-[#33B2FF] shrink-0 mt-0.5" />
          <div>
            <div className="text-white font-medium mb-2">{_t("Certificazione blockchain (Polygon)")}</div>
            <p className="text-sm text-white/75 leading-relaxed">{_t("Al sigillo verrà registrato automaticamente su")}{" "}<strong className="text-[#33B2FF]">{_t("blockchain pubblica Polygon")}</strong>{" "}{_t("un hash crittografico del contenuto, la data di sigillo e la data programmata di apertura. Riceverai un")}{" "}<strong className="text-white">{_t("certificato univoco scaricabile")}</strong>{" "}{_t("con il numero di transazione blockchain come prova di inviolabilità.")}{" "}<strong className="text-white">{_t("Nessuna modifica alla data sarà possibile dopo la registrazione.")}</strong>
            </p>
          </div>
        </div>
        <label className="flex items-start gap-3 cursor-pointer">
          <input data-testid="launch-confirm-blockchain" type="checkbox" checked={confirmedBlockchain} onChange={(e) => setConfirmedBlockchain(e.target.checked)} className="mt-1 accent-[#33B2FF]" />
          <span className="text-sm text-white">{_t("Autorizzo la registrazione dell'hash del contenuto e delle date sulla blockchain Polygon come prova di inviolabilità.")}</span>
        </label>
      </div>

      <div className="flex justify-between items-center">
        <button data-testid="wizard-step6-prev" onClick={onPrev} className="btn-ghost"><ChevronLeft className="w-4 h-4" />{" "}{_t("Indietro")}</button>
        <button data-testid="launch-btn" disabled={!canLaunch} onClick={() => onLaunch(openDate, tz)} className="btn-launch !text-base !px-8">
          <Rocket className="w-5 h-5 -rotate-45" />{" "}{_t("Lancia nel futuro")}</button>
      </div>
    </div>
  );
}

function Summary({ label, value }) {
  return (
    <div className="p-4 rounded-xl border border-white/10 bg-white/2">
      <div className="text-[10px] uppercase tracking-[0.3em] text-white/40 mb-1">{label}</div>
      <div className="text-white font-medium truncate">{value || "—"}</div>
    </div>
  );
}

// -------------- Launch animation --------------
function LaunchAnimation({ onDone }) {
  const [n, setN] = useState(10);
  const [phase, setPhase] = useState("countdown"); // countdown | launch | done

  useEffect(() => {
    if (phase === "countdown") {
      if (n === 0) {
        setPhase("launch");
        setTimeout(() => setPhase("done"), 2500);
        return;
      }
      const id = setTimeout(() => setN((v) => v - 1), 800);
      return () => clearTimeout(id);
    }
    if (phase === "done") {
      const id = setTimeout(onDone, 1200);
      return () => clearTimeout(id);
    }
  }, [n, phase, onDone]);

  return (
    <div className="fixed inset-0 z-[100] flex flex-col items-center justify-center bg-[#070B15]" data-testid="launch-animation">
      <div className="starfield absolute inset-0 opacity-60" />
      {phase === "countdown" && (
        <div className="text-center">
          <div className="text-[10px] uppercase tracking-[0.4em] text-[#FF5722] mb-4">{_t("Conto alla rovescia")}</div>
          <div key={n} className="font-mono-launch text-[180px] leading-none font-bold text-[#FF5722] launch-glow number-pop">{n === 0 ? "0" : n}</div>
        </div>
      )}
      {phase === "launch" && (
        <div className="text-center">
          <img src="/images/img2.png" alt={_t("Rocket launching")} className="w-48 h-48 mx-auto lift-off" />
          <div className="font-display text-4xl md:text-6xl text-[#FF5722] mt-4 launch-glow">{_t("Decollo!")}</div>
          <div className="mt-4 text-white/60 font-body">{_t("La tua capsula è partita.")}</div>
        </div>
      )}
      {phase === "done" && (
        <div className="text-center">
          <div className="font-display text-3xl md:text-4xl text-white mb-3">{_t("In viaggio nel tempo")}</div>
          <div className="text-white/60 text-sm">{_t("Hai 24 ore per annullare, poi sarà sigillata per sempre.")}</div>
        </div>
      )}
    </div>
  );
}
